import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { z } from 'zod';

import type { ActiveFormDescriptorField, CoerceRejectReason } from './types';

// Strict parsing (below) needs the customParseFormat plugin; without it dayjs
// parses leniently and overflows invalid calendar dates (e.g. 2026-02-30 →
// 2026-03-02). Extension is idempotent.
dayjs.extend(customParseFormat);

export type CoerceResult =
  | { ok: true; value: unknown }
  | { ok: false; reason: CoerceRejectReason };

/**
 * Display types this cut can safely coerce and prefill. Rating/enum, users,
 * tags, departments, files and custom attributes are deliberately excluded:
 *
 * - rating/enum: `displayType: 'rating'` covers BOTH numeric-taxonomy ratings
 *   (e.g. Priority → `number`) and string-enum radio fields (e.g. Status →
 *   an enum string), which the form config alone cannot disambiguate — writing
 *   the wrong value type would corrupt the field. Deferred to a follow-up.
 * - users/tags/departments/files/custom attributes: relational values needing
 *   name→UUID resolution into bespoke shapes; out of scope for cut 1.
 */
export const SUPPORTED_DISPLAY_TYPES = [
  'text',
  'textarea',
  'date',
  'number',
] as const;

// A string, or a primitive the model may send unquoted (number/boolean),
// stringified. Non-primitives (null/undefined/objects/arrays) fail the union.
const stringSchema = z
  .union([z.string(), z.number(), z.boolean()])
  .transform((value) => String(value));

// A date in `YYYY-MM-DD` (or its unpadded `YYYY-M-D` variant), normalised to the
// `YYYY-MM-DD` shape every date field's schema expects (see `StringDateSchema` in
// src/schemas/global.ts). Parsed STRICTLY so impossible calendar dates
// (2026-02-30) and non-date strings are rejected rather than overflowed; also
// rejects non-strings.
const DATE_INPUT_FORMATS = ['YYYY-MM-DD', 'YYYY-M-D'];
const dateSchema = z
  .string()
  .refine((value) => dayjs(value, DATE_INPUT_FORMATS, true).isValid())
  .transform((value) =>
    dayjs(value, DATE_INPUT_FORMATS, true).format('YYYY-MM-DD')
  );

// A finite number, or a non-empty numeric string. Empty strings, null and
// non-numeric strings are rejected (unlike a bare `z.coerce.number()`, which
// would turn `''`/`null` into 0).
const numberSchema = z
  .union([z.number(), z.string().trim().min(1)])
  .pipe(z.coerce.number())
  .refine((value) => Number.isFinite(value));

const SCHEMA_BY_DISPLAY_TYPE: Record<string, z.ZodType> = {
  text: stringSchema,
  textarea: stringSchema,
  date: dateSchema,
  number: numberSchema,
};

/**
 * Coerce a raw agent-proposed value into the shape the given field expects,
 * keyed on the field's `displayType`, via a per-type zod schema (mirrors the
 * `z.coerce` / `z.preprocess` coercion used in the data-import package). Pure
 * and side-effect free — the caller (`createApplyPrefill`) writes the result.
 * Rejects (never silently drops) unsupported types and unparseable values so
 * the UI can report exactly what did not take.
 */
export const coerceFieldValue = (
  field: ActiveFormDescriptorField,
  raw: unknown
): CoerceResult => {
  const schema = SCHEMA_BY_DISPLAY_TYPE[field.displayType];
  if (!schema) {
    return { ok: false, reason: 'unsupported_type' };
  }

  const parsed = schema.safeParse(raw);

  return parsed.success
    ? { ok: true, value: parsed.data }
    : { ok: false, reason: 'invalid_value' };
};
